import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import APP_COLOURS from '../config/colors';
import moment from 'moment';
import { getAvatarImage } from '../config/avatars';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';

const PublicLeaderboard = () => {
  const { token } = useParams();
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchLeaderboardData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchLeaderboardData, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const fetchLeaderboardData = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.GET_LEADERBOARD_DATA(token)}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch leaderboard data');
      }

      const data = await response.json();
      setLeaderboardData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return 'R ' + parseFloat(amount || 0).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  };

  const formatAttendanceTime = (timeData) => {
    if (!timeData) return '-';
    
    let timeValue;
    if (timeData.toDate) {
      timeValue = moment(timeData.toDate());
    } else if (typeof timeData === 'string') {
      timeValue = moment(timeData);
    } else if (timeData._seconds) {
      timeValue = moment.unix(timeData._seconds);
    } else {
      return '-';
    }
    
    return timeValue.format('HH:mm');
  };

  if (loading && !leaderboardData) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: APP_COLOURS.BG2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div 
            style={{ 
              width: '60px', 
              height: '60px', 
              border: `4px solid ${APP_COLOURS.BLUE}`,
              borderTop: '4px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }}
          ></div>
          <p style={{ color: APP_COLOURS.TEXTCOLOR, fontSize: '18px' }}>Loading Leaderboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: APP_COLOURS.BG2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{ 
          textAlign: 'center',
          maxWidth: '500px',
          backgroundColor: APP_COLOURS.CARD,
          borderRadius: '16px',
          padding: '40px',
          border: `1px solid ${APP_COLOURS.ERROR}40`
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: `${APP_COLOURS.ERROR}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <i className="fas fa-exclamation-triangle" style={{ color: APP_COLOURS.ERROR, fontSize: '24px' }}></i>
          </div>
          <h2 style={{ color: APP_COLOURS.TEXTCOLOR, marginBottom: '10px' }}>Access Denied</h2>
          <p style={{ color: APP_COLOURS.TEXTCOLOR, opacity: 0.7 }}>{error}</p>
        </div>
      </div>
    );
  }

  const leaderboard = leaderboardData?.leaderboard || [];
  const summary = leaderboardData?.summary || {};
  const manager = leaderboardData?.manager || {};

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: APP_COLOURS.BG2,
      padding: '20px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          backgroundColor: APP_COLOURS.CARD,
          borderRadius: '16px',
          padding: '30px',
          marginBottom: '20px',
          border: `1px solid ${APP_COLOURS.BLUE}20`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
            <div>
              <h1 style={{ 
                color: APP_COLOURS.TEXTCOLOR, 
                fontSize: '32px', 
                fontWeight: 'bold',
                margin: '0 0 10px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '36px' }}>🏆</span>
                Live Leaderboard
              </h1>
              <p style={{ color: APP_COLOURS.TEXTCOLOR, opacity: 0.7, margin: 0, fontSize: '14px' }}>
                {manager.first_name} {manager.last_name} • {moment(leaderboardData?.date).format('MMMM Do, YYYY')}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ 
                fontSize: '28px', 
                fontWeight: 'bold', 
                color: APP_COLOURS.BLUE,
                fontVariantNumeric: 'tabular-nums'
              }}>
                {formatTime(currentTime)}
              </div>
              <p style={{ color: APP_COLOURS.TEXTCOLOR, opacity: 0.6, margin: '5px 0 0 0', fontSize: '12px' }}>
                Auto-refreshes every 30s
              </p>
            </div>
          </div>

          {/* Summary Stats */}
          <div style={{ 
            display: 'flex', 
            gap: '20px', 
            marginTop: '20px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              flex: '1',
              minWidth: '150px',
              backgroundColor: `${APP_COLOURS.GREEN}10`,
              borderRadius: '12px',
              padding: '15px',
              border: `1px solid ${APP_COLOURS.GREEN}30`
            }}>
              <div style={{ fontSize: '12px', color: APP_COLOURS.TEXTCOLOR, opacity: 0.7, marginBottom: '5px' }}>
                Total Sales
              </div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: APP_COLOURS.GREEN }}>
                {formatCurrency(summary.totalSales)}
              </div>
            </div>
            <div style={{
              flex: '1',
              minWidth: '150px',
              backgroundColor: `${APP_COLOURS.BLUE}10`,
              borderRadius: '12px',
              padding: '15px',
              border: `1px solid ${APP_COLOURS.BLUE}30`
            }}>
              <div style={{ fontSize: '12px', color: APP_COLOURS.TEXTCOLOR, opacity: 0.7, marginBottom: '5px' }}>
                Transactions
              </div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: APP_COLOURS.BLUE }}>
                {summary.totalTransactions || 0}
              </div>
            </div>
            <div style={{
              flex: '1',
              minWidth: '150px',
              backgroundColor: `${APP_COLOURS.PURPLE}10`,
              borderRadius: '12px',
              padding: '15px',
              border: `1px solid ${APP_COLOURS.PURPLE}30`
            }}>
              <div style={{ fontSize: '12px', color: APP_COLOURS.TEXTCOLOR, opacity: 0.7, marginBottom: '5px' }}>
                Active Staff
              </div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: APP_COLOURS.PURPLE }}>
                {summary.staffCount || 0}
              </div>
            </div>
          </div>
        </div>

        {/* Leaderboard */}
        <div style={{
          backgroundColor: APP_COLOURS.CARD,
          borderRadius: '16px',
          padding: '20px',
          border: `1px solid ${APP_COLOURS.BLUE}20`,
        }}>
          {leaderboard.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: `${APP_COLOURS.BLUE}10`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <i className="fas fa-user-clock" style={{ color: APP_COLOURS.BLUE, fontSize: '32px', opacity: 0.5 }}></i>
              </div>
              <p style={{ color: APP_COLOURS.TEXTCOLOR, opacity: 0.7, fontSize: '16px' }}>
                No staff online today
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0' }}>
                <thead>
                  <tr style={{ 
                    backgroundColor: APP_COLOURS.BG3,
                    borderBottom: `2px solid ${APP_COLOURS.BG4}`
                  }}>
                    <th style={{ textAlign: 'center', padding: '15px 10px', fontSize: '14px', fontWeight: '600', color: APP_COLOURS.TEXTCOLOR, width: '60px' }}>
                      Rank
                    </th>
                    <th style={{ textAlign: 'left', padding: '15px 10px', fontSize: '14px', fontWeight: '600', color: APP_COLOURS.TEXTCOLOR }}>
                      Staff Member
                    </th>
                    <th style={{ textAlign: 'center', padding: '15px 10px', fontSize: '14px', fontWeight: '600', color: APP_COLOURS.TEXTCOLOR, width: '100px' }}>
                      Last Seen
                    </th>
                    <th style={{ textAlign: 'center', padding: '15px 10px', fontSize: '14px', fontWeight: '600', color: APP_COLOURS.TEXTCOLOR, width: '90px' }}>
                      Check-in
                    </th>
                    <th style={{ textAlign: 'center', padding: '15px 10px', fontSize: '14px', fontWeight: '600', color: APP_COLOURS.TEXTCOLOR, width: '120px' }}>
                      Lunch
                    </th>
                    <th style={{ textAlign: 'center', padding: '15px 10px', fontSize: '14px', fontWeight: '600', color: APP_COLOURS.TEXTCOLOR, width: '90px' }}>
                      Check-out
                    </th>
                    <th style={{ textAlign: 'right', padding: '15px 10px', fontSize: '14px', fontWeight: '600', color: APP_COLOURS.TEXTCOLOR, width: '150px' }}>
                      Total Sales
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((staff, idx) => {
                    const rank = idx + 1;
                    const sales = staff.sales?.totalSales || 0;
                    const transactions = staff.sales?.totalTransactions || 0;
                    const isTopThree = rank <= 3;
                    const isLeader = rank === 1;

                    // Check if last seen was today
                    const isSeenToday = staff.last_seen && staff.last_seen._seconds 
                      ? moment.unix(staff.last_seen._seconds).isSame(moment(), 'day')
                      : staff.last_seen
                      ? moment(staff.last_seen).isSame(moment(), 'day')
                      : false;
                    
                    // Determine status color: green if seen today, orange/yellow if not
                    const statusColor = isSeenToday ? APP_COLOURS.SUCCESS : APP_COLOURS.WARNING || APP_COLOURS.ORANGE;

                    return (
                      <tr
                        key={staff.id || idx}
                        style={{
                          backgroundColor: isLeader ? `${APP_COLOURS.WARNING || APP_COLOURS.ORANGE}08` : 
                                          isTopThree ? `${APP_COLOURS.BLUE}05` : 
                                          idx % 2 === 0 ? APP_COLOURS.BG3 : 'transparent',
                          borderBottom: `1px solid ${APP_COLOURS.BG4}20`,
                        }}
                      >
                        {/* Rank */}
                        <td style={{ padding: '20px 10px', textAlign: 'center' }}>
                          {isLeader ? (
                            <span style={{ fontSize: '32px' }}>🥇</span>
                          ) : rank === 2 ? (
                            <span style={{ fontSize: '32px' }}>🥈</span>
                          ) : rank === 3 ? (
                            <span style={{ fontSize: '32px' }}>🥉</span>
                          ) : (
                            <span style={{ fontSize: '18px', fontWeight: '600', color: APP_COLOURS.TEXTCOLOR, opacity: 0.5 }}>
                              {rank}
                            </span>
                          )}
                        </td>

                        {/* Staff Info */}
                        <td style={{ padding: '20px 10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <div style={{ position: 'relative' }}>
                              <img
                                src={getAvatarImage(staff.avatar || '1')}
                                alt={`${staff.first_name} ${staff.last_name}`}
                                style={{
                                  width: '50px',
                                  height: '50px',
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  border: isTopThree ? `3px solid ${APP_COLOURS.BLUE}` : `3px solid ${APP_COLOURS.BG4}`,
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  const fallback = e.target.nextSibling;
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                              />
                              <div
                                style={{
                                  width: '50px',
                                  height: '50px',
                                  borderRadius: '50%',
                                  background: `linear-gradient(135deg, ${APP_COLOURS.BLUE}, ${APP_COLOURS.PURPLE})`,
                                  border: isTopThree ? `3px solid ${APP_COLOURS.BLUE}` : `3px solid ${APP_COLOURS.BG4}`,
                                  display: 'none',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#fff',
                                  fontWeight: 'bold',
                                  fontSize: '16px',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0
                                }}
                              >
                                {(staff.first_name?.[0] || '') + (staff.last_name?.[0] || '')}
                              </div>
                              <div
                                style={{
                                  position: 'absolute',
                                  bottom: '-2px',
                                  right: '-2px',
                                  width: '14px',
                                  height: '14px',
                                  borderRadius: '50%',
                                  backgroundColor: statusColor,
                                  border: `2px solid ${APP_COLOURS.BG}`,
                                  animation: 'pulse 2s infinite'
                                }}
                              ></div>
                            </div>
                            <div>
                              <p style={{ 
                                fontWeight: '600', 
                                fontSize: isLeader ? '20px' : '16px',
                                color: APP_COLOURS.TEXTCOLOR,
                                margin: '0 0 4px 0'
                              }}>
                                {staff.first_name} {staff.last_name}
                              </p>
                              <p style={{ 
                                fontSize: '13px', 
                                opacity: 0.6, 
                                color: APP_COLOURS.TEXTCOLOR,
                                margin: 0
                              }}>
                                {staff.position || 'Staff'}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Last Seen */}
                        <td style={{ padding: '20px 10px', textAlign: 'center' }}>
                          <div style={{ fontSize: '11px', fontWeight: '100', color: APP_COLOURS.TEXTCOLOR, opacity: 0.6, marginBottom: '2px' }}>
                            Last seen
                          </div>
                          <div style={{ 
                            fontSize: '13px', 
                            fontWeight: '600', 
                            color: APP_COLOURS.TEXTCOLOR,
                            fontStyle: staff.last_seen ? 'normal' : 'italic'
                          }}>
                            {staff.last_seen && staff.last_seen._seconds
                              ? moment.unix(staff.last_seen._seconds).fromNow()
                              : staff.last_seen 
                              ? moment(staff.last_seen).fromNow()
                              : 'unavailable'}
                          </div>
                        </td>

                        {/* Check-in */}
                        <td style={{ padding: '20px 10px', textAlign: 'center' }}>
                          {staff.attendance?.clockIn ? (
                            <div>
                              <div style={{ fontSize: '11px', fontWeight: '100', color: APP_COLOURS.TEXTCOLOR, opacity: 0.6, marginBottom: '2px' }}>
                                Check-in
                              </div>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: APP_COLOURS.SUCCESS }}>
                                {formatAttendanceTime(staff.attendance.clockIn)}
                              </div>
                            </div>
                          ) : (
                            <div style={{ fontSize: '14px', color: APP_COLOURS.TEXTCOLOR, opacity: 0.3 }}>
                              -
                            </div>
                          )}
                        </td>

                        {/* Lunch */}
                        <td style={{ padding: '20px 10px', textAlign: 'center', opacity: staff.attendance?.lunchStart ? 1 : 0.3 }}>
                          {staff.attendance?.lunchStart ? (
                            <div>
                              <div style={{ fontSize: '11px', fontWeight: '100', color: APP_COLOURS.TEXTCOLOR, opacity: 0.6, marginBottom: '2px' }}>
                                Lunch
                              </div>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: APP_COLOURS.TEXTCOLOR }}>
                                {formatAttendanceTime(staff.attendance.lunchStart)}
                                {' - '}
                                {staff.attendance?.lunchEnd ? formatAttendanceTime(staff.attendance.lunchEnd) : '_'}
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div style={{ fontSize: '11px', fontWeight: '100', color: APP_COLOURS.TEXTCOLOR, opacity: 0.6, marginBottom: '2px' }}>
                                Lunch
                              </div>
                              <div style={{ fontSize: '13px', color: APP_COLOURS.TEXTCOLOR }}>
                                _ - _
                              </div>
                            </div>
                          )}
                        </td>

                        {/* Check-out */}
                        <td style={{ padding: '20px 10px', textAlign: 'center', opacity: staff.attendance?.clockOut ? 1 : 0.3 }}>
                          {staff.attendance?.clockOut ? (
                            <div>
                              <div style={{ fontSize: '11px', fontWeight: '100', color: APP_COLOURS.TEXTCOLOR, opacity: 0.6, marginBottom: '2px' }}>
                                Check-out
                              </div>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: APP_COLOURS.TEXTCOLOR }}>
                                {formatAttendanceTime(staff.attendance.clockOut)}
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div style={{ fontSize: '11px', fontWeight: '100', color: APP_COLOURS.TEXTCOLOR, opacity: 0.6, marginBottom: '2px' }}>
                                Check-out
                              </div>
                              <div style={{ fontSize: '14px', color: APP_COLOURS.TEXTCOLOR }}>
                                _
                              </div>
                            </div>
                          )}
                        </td>

                        {/* Sales */}
                        <td style={{ padding: '20px 10px', textAlign: 'right' }}>
                          <div>
                            <p style={{ 
                              fontWeight: 'bold', 
                              fontSize: isLeader ? '24px' : '18px',
                              color: sales > 0 ? APP_COLOURS.GREEN : APP_COLOURS.TEXTCOLOR,
                              opacity: sales > 0 ? 1 : 0.3,
                              margin: 0
                            }}>
                              {formatCurrency(sales)}
                            </p>
                            {transactions > 0 && (
                              <p style={{ 
                                fontSize: '12px',
                                color: APP_COLOURS.TEXTCOLOR, 
                                opacity: 0.6,
                                margin: '4px 0 0 0'
                              }}>
                                {transactions} {transactions === 1 ? 'transaction' : 'transactions'}
                              </p>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default PublicLeaderboard;

